import { DroppedItemGlint, GlintSubject } from "./effects/dropped_item_glint"
import { entityAppearanceKey, updateEntityArtwork } from "./entity_artwork"
import { Mechanics } from "game-common/mechanics/mechanics"
import { ScalingParams } from "../../../client_util"
import { applyStairTravel, stairTravelForRendering } from "./effects/stair_travel"
import { applyFloorFall, floorFallZIndex, floorFallForRendering } from "./effects/floor_fall"
import { Container, Rectangle, filters } from "pixi.js"
import { collisionMover, Direction, EmberRosterRole, Entity, Light, LightsContainer, SceneRoomState, TileSize } from "game-common/models"
import { ApplyInputResultPool } from "game-common/apply_input_result_pool"
import { TileMapMetaProvider } from "game-common/tile_map_meta_provider"
import { companionFloorOffset, insideHouse, TwoStoreyHouse } from "game-common/room/ember/two_storey_house"
import type { ClientRenderable } from "./entity_rendering_manager"
import { WorldOverlayItem } from "./world_overlay_surface"
import { VisibilityFade } from "./visibility_fade"
import { isExteriorFloorFall } from "game-common/floor_fall"

interface PositionSample { ts: number; x: number; y: number; direction: Direction; angle: number }

interface SceneMap {
    overlapsRenderedMap?(bounds: Rectangle): boolean
    setProjectionFloor?(floor: number): void
    setViewEntity?(entity: Entity): void
    update(map: TileMapMetaProvider): void
    renderTileMap(): void
    queueMapDelta(deltas: SceneRoomState["mapDeltas"]): void
    rerender(): void
    destroy(): void
}

/** A passive view of the other simulation room, using the normal tile and entity artwork. */
export class SceneFloorRenderer {
    private roomId?: string
    root?: Container
    map?: SceneMap
    provider?: TileMapMetaProvider
    private groundBlur?: InstanceType<typeof filters.BlurFilter>
    private groundShade?: InstanceType<typeof filters.ColorMatrixFilter>
    private groundBrightness?: number
    private clockOffset?: number
    private visibilityFade = new VisibilityFade()
    private entities = new Map<string, { entity: Entity; sprite: ClientRenderable; overlay: WorldOverlayItem;
        samples: PositionSample[]; glint?: DroppedItemGlint; appearance?: string; bulletTs?: number; bulletStopped?: boolean }>()

    constructor(private surface: Container, private createMap: (root: Container, sky: Container) => SceneMap,
        private createEntity: (entity: Entity) => ClientRenderable, private observation = false,
        private retainEntity?: (entity: Entity, sprite: ClientRenderable) => boolean,
        private getRole: (id: string) => EmberRosterRole | null = () => null) {}

    clear(): void {
        this.entities.forEach(({ entity, sprite, overlay, glint }) => {
            if (!this.retainEntity?.(entity, sprite)) { sprite.cleanup?.(); sprite.destroy({ children: true }) }
            glint?.destroy({ children: true })
            overlay.destroy({ children: true })
        })
        this.entities.clear()
        this.map?.destroy()
        this.root?.parent?.removeChild(this.root)
        this.root?.destroy({ children: true })
        this.groundBlur?.destroy()
        this.groundBlur = undefined
        this.groundShade?.destroy()
        this.groundShade = undefined
        this.groundBrightness = undefined
        this.map = undefined
        this.root = undefined
        this.provider = undefined
        this.roomId = undefined
        this.clockOffset = undefined
        this.visibilityFade.reset()
    }

    update(frames: SceneRoomState[], active?: TwoStoreyHouse): void {
        const frame = frames[0]
        if ((!active && !this.observation) || !frame) { this.clear(); return }
        if (this.roomId !== frame.roomId) this.clear()
        if (!this.root) {
            if (!frame.tilemaps || !frame.tilemapMeta) return
            this.roomId = frame.roomId
            this.root = new Container()
            this.root.sortableChildren = true
            this.root.interactiveChildren = false
            const sky = new Container()
            sky.zIndex = 1000000
            this.root.addChild(sky)
            this.surface.addChild(this.root)
            this.map = this.createMap(this.root, sky)
        }
        if (frame.tilemaps && frame.tilemapMeta) {
            // Full snapshots also arrive after roof/floor deletions. Keep the container,
            // fade and entity playback buffers when refreshing the same passive room.
            this.provider = new TileMapMetaProvider(frame.roomId, frame.tilemapMeta, frame.tilemaps, undefined)
            this.map.setProjectionFloor?.(this.observation ? this.provider.twoStoreyHouse?.floor : active.floor)
            this.map.update(this.provider)
            this.map.renderTileMap()
        }
        if (!this.root || !this.provider) return
        if (this.observation) active = this.provider.getHousePresentation()
        if (!active) return
        const now = Date.now()
        const timestamp = frame.serverTs ?? now - (this.clockOffset ?? 0)
        const offset = now - timestamp
        this.clockOffset = Math.min(this.clockOffset ?? offset, offset)
        this.root.y = this.observation ? 0 : companionFloorOffset(active, this.provider.twoStoreyHouse)
        const floor = this.provider.twoStoreyHouse.floor
        this.root.zIndex = floor < active.floor ? -1000 + floor : 100 + (active.top + active.depth) * TileSize + floor
        const blur = this.backgroundBlur(active.floor - floor)
        this.root.filters = blur ? [blur] : null
        if (frame.mapDeltas?.length) {
            this.map.queueMapDelta(frame.mapDeltas)
            this.map.rerender()
            this.map.renderTileMap()
        }
        const present = new Set<string>()
        for (const message of frame.messages) {
            if (!message.position || message.entityType === "system") continue
            if (message.entityType === "bullet" && message.entityState === "dead") continue
            present.add(message.entityId)
            let item = this.entities.get(message.entityId)
            if (!item) {
                const entity = new Entity()
                Object.assign(entity, message)
                entity.location = Object.assign({ x: message.position[0], y: message.position[1] }, { roomId: frame.roomId })
                const sprite = this.createEntity(entity)
                const overlay = new WorldOverlayItem()
                sprite.setWorldOverlayItem(overlay)
                overlay.scale.set(1 / ScalingParams.surfaceZoom)
                overlay.zIndex = 2000000
                this.root.addChild(overlay)
                sprite.interactive = false
                sprite.interactiveChildren = false
                this.root.addChild(sprite)
                item = { entity, sprite, overlay, samples: [],
                    bulletTs: entity.entityType === "bullet" ? now : undefined }
                this.entities.set(message.entityId, item)
            }
            const { entity, sprite } = item
            // Stair playback uses current phase coordinates, so old delayed samples
            // must not pull the actor back toward the opening when the walk finishes.
            if (message.stairTravel || entity.stairTravel) item.samples.length = 0
            updateEntityArtwork(sprite, message, entity)
            Object.assign(entity, message)
            entity.floorFall = message.floorFall
            entity.stairTravel = message.stairTravel
            if (entity.entityType !== "bullet") {
                // Keep authoritative positions off the drawn entity between snapshots.
                const previous = item.samples[item.samples.length - 1]
                const sample = { ts: timestamp, x: message.position[0], y: message.position[1],
                    direction: message.direction, angle: message.angle }
                if (previous && Math.hypot(sample.x - previous.x, sample.y - previous.y) > 4 * TileSize) {
                    item.samples.length = 0
                    entity.location = { x: sample.x, y: sample.y }
                }
                if (item.samples.length && item.samples[item.samples.length - 1].ts >= timestamp) item.samples.pop()
                item.samples.push(sample)
                if (item.samples.length > 32) item.samples.shift()
            }
            entity.movement.direction = message.direction
            entity.movement.angle = message.angle
            entity.roomId = frame.roomId
            entity.maxHp = message.hpMax
            if (this.observation) { sprite.update?.(entity.hp, entity.maxHp); sprite.updateName?.(entity.name || "") }
            const appearance = entityAppearanceKey(entity)
            if (appearance !== item.appearance) { sprite.updatePaperdoll(entity); item.appearance = appearance }
            sprite.recolor(message.color ?? 0xffffff)
            sprite.updateScale(message.spriteScale ?? 1)
            sprite.visible = message.visible !== false && !item.bulletStopped
            const subject = sprite as ClientRenderable & Partial<GlintSubject>
            if (entity.npcType === "collectable" && subject.glintArt) {
                if (entity.noGlint === true) {
                    item.glint?.destroy({ children: true })
                    item.glint = undefined
                } else if (!item.glint) {
                    item.glint = new DroppedItemGlint(entity.entityId, subject as GlintSubject)
                    // Above this floor's art, while inheriting its projection, shade and occlusion.
                    item.glint.zIndex = 2000001
                    this.root.addChild(item.glint)
                }
            }
        }
        this.entities.forEach((item, id) => {
            if (present.has(id)) return
            item.sprite.parent?.removeChild(item.sprite)
            item.sprite.cleanup?.()
            item.sprite.destroy({ children: true })
            item.glint?.destroy({ children: true })
            item.overlay.destroy({ children: true })
            this.entities.delete(id)
        })
    }

    private backgroundBlur(distance: number): InstanceType<typeof filters.BlurFilter> | undefined {
        if (this.observation || distance < 2) return undefined
        // Adjacent floors stay sharp; more distant floors soften with a readability cap.
        this.groundBlur ||= new filters.BlurFilter(0.75, 2)
        this.groundBlur.blur = Math.min(2.25, 0.75 + (distance - 1) * 0.5)
        return this.groundBlur
    }

    getEntity(id: string): Entity | undefined { return this.entities.get(id)?.entity }
    getRenderable(id: string): ClientRenderable | undefined { return this.entities.get(id)?.sprite }

    getLights(): LightsContainer {
        const lights = new LightsContainer()
        this.entities.forEach(({ entity }) => {
            if ((entity.lightMeta || entity.npcType === "light") && entity.visible !== false && !entity.isDead()) lights.set(entity.entityId,
                { entityId: entity.entityId, location: entity.location,
                    radius: entity.lightRadius(), intensity: entity.lightIntensity() })
        })
        return lights
    }

    render(active: TwoStoreyHouse | undefined, player: Entity): void {
        if (this.observation) active = this.provider?.getHousePresentation()
        if (!this.root || !active || !player?.location) return
        if (this.observation) this.map?.setViewEntity?.(player)
        this.root.y = this.observation ? 0 : companionFloorOffset(active, this.provider.twoStoreyHouse)
        const floor = this.provider.twoStoreyHouse.floor
        this.root.zIndex = floor < active.floor ? -1000 + floor : 100 + (active.top + active.depth) * TileSize + floor
        const enclosed = !isExteriorFloorFall(player.floorFall) && insideHouse(active, player.location)
        this.visibilityFade.update(this.observation || floor < active.floor || !enclosed)
        this.root.alpha = this.visibilityFade.alpha
        this.root.visible = this.visibilityFade.visible
        const blur = this.backgroundBlur(active.floor - floor)
        const backgroundFilters = blur ? [blur] : []
        if (!this.observation && floor < active.floor) {
            if (enclosed) {
                this.groundShade ||= new filters.ColorMatrixFilter()
                // Keep distant rooms readable while deepening the shade per storey.
                const brightness = Math.max(0.5, Math.pow(0.88, active.floor - floor))
                if (brightness !== this.groundBrightness) {
                    this.groundShade.brightness(brightness, false)
                    this.groundBrightness = brightness
                }
                this.root.filters = [...backgroundFilters, this.groundShade]
            } else {
                this.root.filters = blur ? backgroundFilters : null
            }
        } else {
            this.root.filters = null
        }
        this.map?.setProjectionFloor?.(active.floor)
        this.map?.renderTileMap()
        // Match the active room's playback delay, covering the companion's 100ms snapshot gap.
        const timestamp = Date.now() - (this.clockOffset ?? 0) - 150
        const now = Date.now()
        this.entities.forEach(item => {
            const { entity, sprite, overlay, samples } = item
            // Roster updates can arrive without a new room snapshot.
            sprite.updateRole?.(this.getRole(entity.entityId))
            if (entity.entityType === "bullet") {
                // Match active-floor bullets: integrate every frame from the first
                // received position, rather than waiting on delayed snapshots. Keep
                // a locally finished bullet hidden until its authoritative removal.
                const dt = Math.max(0, now - (item.bulletTs ?? now)) / 1000
                item.bulletTs = now
                if (!item.bulletStopped && dt > 0 && (entity.speed2.x || entity.speed2.y)) {
                    const result = ApplyInputResultPool.instance.checkout()
                    try {
                        entity.applyInput(undefined, dt, entity.speed2, result.data,
                            collisionMover(this.provider, false, false), true)
                        item.bulletStopped = (!result.data.movedX && !result.data.movedY) ||
                            (entity.maxDistance !== undefined && entity.travelledDistance() > entity.maxDistance)
                    } finally {
                        ApplyInputResultPool.instance.release(result)
                    }
                }
                sprite.visible = entity.visible !== false && !item.bulletStopped
            } else {
                while (samples.length >= 2 && samples[1].ts <= timestamp) samples.shift()
                const from = samples[0]
                if (!from) return
                const to = samples[1]
                const progress = to && timestamp >= from.ts
                    ? Math.min(1, (timestamp - from.ts) / (to.ts - from.ts)) : 0
                entity.location.x = from.x + ((to?.x ?? from.x) - from.x) * progress
                entity.location.y = from.y + ((to?.y ?? from.y) - from.y) * progress
                const facing = progress > 0 ? to : from
                entity.movement.direction = facing.direction
                entity.movement.angle = facing.angle
            }
            sprite.updateRenderedLocationFrom(entity)
            const fallView = floorFallForRendering(entity, sprite, this.root.visible && (sprite.isReadyToShow?.() ?? true), Date.now(), this.roomId)
            sprite.doMovement(stairTravelForRendering(fallView, sprite, Date.now(), this.roomId))
            applyFloorFall(fallView, sprite)
            applyStairTravel(entity, sprite, Date.now(), this.roomId)
            sprite.zIndex = floorFallZIndex(fallView, 100 + entity.location.y)
            // The floor root uses world coordinates; only the HUD's artwork needs
            // compensation for its normal screen-space scale.
            overlay.x = entity.location.x - TileSize * .25
            overlay.y = entity.location.y - TileSize * (Mechanics.entity.body.isShort(entity) ? .75 : 1.25)
            overlay.visible = sprite.visible
            // Floor roots use world pixels; the active HUD overlay uses screen pixels.
            item.glint?.animate(now, 1)
        })
    }
}

/** Each passive floor retains its own artwork and movement buffer. */
export class SceneRoomRenderer {
    private floors = new Map<string, SceneFloorRenderer>()
    // The first companion remains accessible for map previews and diagnostics.
    get root(): Container | undefined { return this.floors.values().next().value?.root }
    get map(): SceneMap | undefined { return this.floors.values().next().value?.map }

    constructor(private surface: Container, private createMap: (root: Container, sky: Container) => SceneMap,
        private createEntity: (entity: Entity) => ClientRenderable,
        private retainEntity?: (entity: Entity, sprite: ClientRenderable) => boolean,
        private getRole: (id: string) => EmberRosterRole | null = () => null) {}

    clear(): void {
        this.floors.forEach(floor => floor.clear())
        this.floors.clear()
    }

    activateRoom(roomId: string): void {
        // This floor will now be drawn by the active entity manager. Retire its
        // passive map while handing its loaded bodies to that manager.
        this.floors.get(roomId)?.clear()
        this.floors.delete(roomId)
    }

    getEntity(id: string): Entity | undefined {
        for (const floor of this.floors.values()) {
            const entity = floor.getEntity(id)
            if (entity) return entity
        }
    }

    getRenderable(id: string): ClientRenderable | undefined {
        for (const floor of this.floors.values()) {
            const sprite = floor.getRenderable(id)
            if (sprite) return sprite
        }
    }

    getLights(): Light[] {
        const lights: Light[] = []
        this.floors.forEach(floor => {
            if (!floor.root?.visible || floor.root.alpha <= 0) return
            floor.getLights().iterate(light => lights.push({ ...light,
                location: { x: light.location.x, y: light.location.y + floor.root.y },
                intensity: light.intensity * floor.root.alpha }))
        })
        return lights
    }

    update(frames: SceneRoomState[], active?: TwoStoreyHouse): void {
        if (!active || !frames.length) { this.clear(); return }
        const allowed = active.floorRoomIds ?? [active.groundRoomId, active.upperRoomId]
        this.floors.forEach((floor, id) => {
            if (allowed.includes(id)) return
            floor.clear()
            this.floors.delete(id)
        })
        for (const frame of frames) {
            let floor = this.floors.get(frame.roomId)
            if (!floor) {
                floor = new SceneFloorRenderer(this.surface, this.createMap, this.createEntity, false, this.retainEntity, this.getRole)
                this.floors.set(frame.roomId, floor)
            }
            floor.update([frame], active)
        }
    }

    hasForegroundFloor(playerZ: number): boolean {
        for (const floor of this.floors.values()) {
            if (floor.root?.visible && floor.root.alpha > 0.15 && floor.root.zIndex > playerZ) return true
        }
        return false
    }

    /** Bounds are in screen space, like the drawn player frame. Lower floors cannot cover it. */
    occludes(bounds: Rectangle, playerZ: number): boolean {
        for (const floor of this.floors.values()) {
            if (floor.root?.visible && floor.root.alpha > 0.15 && floor.root.zIndex > playerZ &&
                floor.map?.overlapsRenderedMap?.(bounds)) return true
        }
        return false
    }

    linkFloorSupport(active: TileMapMetaProvider): void {
        const providers = new Map<string, TileMapMetaProvider>([[active.roomId, active]])
        this.floors.forEach((floor, id) => { if (floor.provider) providers.set(id, floor.provider) })
        providers.forEach(provider => {
            const house = provider.twoStoreyHouse
            if (!house) return
            provider.setUpperFloorProvider(providers.get(house.floorRoomIds?.[house.floor + 1] ??
                (house.floor === 0 ? house.upperRoomId : undefined)))
            if (house.floor <= 0) return
            const below = house.floorRoomIds?.[house.floor - 1] ?? house.groundRoomId
            provider.setFloorSupportProvider(providers.get(below))
        })
    }

    render(active: TwoStoreyHouse | undefined, player: Entity): void {
        this.floors.forEach(floor => floor.render(active, player))
    }
}
